import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter,
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { signInWithCustomToken, signOut, onAuthStateChanged } from 'firebase/auth';
import axios from 'axios';
import { auth, db } from '../config/firebase';
import { getLevelColorTheme } from '../utils/levelColorTheme';
import { MdSend, MdForum, MdClose } from 'react-icons/md';

const API = process.env.REACT_APP_API_URL || '';

export default function ChatPage({ user: userProp }) {
  const [user, setUser] = useState(userProp);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const messagesRef = useRef([]);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const previousUserIdRef = useRef(null);
  const paginationCooldownRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const isInitializingRef = useRef(false);

  // Keep messagesRef.current in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Scroll to bottom when new messages arrive (not when loading older messages)
  useEffect(() => {
    if (!loading && messages.length > 0 && !loadingMore) {
      const container = messagesContainerRef.current;
      if (container) {
        // Only auto-scroll if we're near the bottom (within 100px) or if it's a new message
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
        if (isNearBottom) {
          // Use setTimeout to ensure DOM is updated
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 0);
        }
      }
    }
  }, [messages.length, loading, loadingMore]);

  // Scroll to bottom on initial load
  useEffect(() => {
    if (!loading && messages.length > 0 && !loadingMore) {
      const container = messagesContainerRef.current;
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 100);
      }
    }
  }, [loading, messages.length, loadingMore]);

  // Reset chat state when user changes (logout/login)
  useEffect(() => {
    const currentUserId = userProp?.id?.toString();
    
    // If user changed (different user logged in) OR user logged out (currentUserId is null)
    if (previousUserIdRef.current !== currentUserId) {
      
      // Unsubscribe from listener first
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      // Clear Firebase auth immediately and wait for it
      const clearFirebase = async () => {
        if (auth?.currentUser) {
          try {
            // Set up observer before signing out
            const signOutPromise = new Promise((resolve) => {
              let resolved = false;
              const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (!user && !resolved) {
                  resolved = true;
                  unsubscribe();
                  resolve();
                }
              });
              setTimeout(() => {
                if (!resolved) {
                  resolved = true;
                  unsubscribe();
                  resolve();
                }
              }, 3000);
            });
            
            await signOut(auth);
            await signOutPromise;
            await new Promise(resolve => setTimeout(resolve, 300));
          } catch (err) {
            // Error signing out from Firebase - silently continue
          }
        }
      };
      clearFirebase();
      
      // Reset all state completely
      setMessages([]);
      setUser(null);
      setFirebaseInitialized(false);
      setError(null);
      setLastVisible(null);
      setHasMore(true);
      setLoadingMore(false);
      messagesRef.current = [];
      setLoading(true);
    }
    
    previousUserIdRef.current = currentUserId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProp?.id]);

  // Fetch user profile and initialize Firebase
  useEffect(() => {
    const initializeChat = async () => {
      // Don't initialize if user is null (logged out)
      if (!userProp) {
        setLoading(false);
        setFirebaseInitialized(false);
        isInitializingRef.current = false;
        return;
      }

      // Prevent multiple simultaneous initializations
      if (isInitializingRef.current) {
        return;
      }

      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // Check if Firebase is configured
      if (!auth || !db) {
        setError('Chat service is not configured. Please check chat settings.');
        setLoading(false);
        isInitializingRef.current = false;
        return;
      }

      try {
        isInitializingRef.current = true;
        setLoading(true);
        setError(null);

        // Fetch user profile to get level info
        let userRes;
        try {
          userRes = await axios.get(`${API}/api/user/profile`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(userRes.data);
        } catch (profileErr) {
          throw new Error(`Failed to load user profile: ${profileErr.response?.data?.error || profileErr.message}`);
        }

        // Get Firebase custom token first (we need it to know which user we're signing in as)
        let tokenRes;
        try {
          tokenRes = await axios.get(`${API}/api/firebaseToken`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { _t: Date.now() } // Cache buster
          });
        } catch (tokenErr) {
          throw new Error(`Failed to get chat service token: ${tokenErr.response?.data?.error || tokenErr.message}`);
        }

        // Get the expected user ID from our app state
        const expectedUserId = userRes.data.id?.toString();

        // Always sign out from Firebase first to ensure clean state
        if (auth.currentUser) {
          // Set up observer BEFORE signing out to catch the state change
          // onAuthStateChanged fires immediately with current state, so we need to ignore that
          const signOutPromise = new Promise((resolve) => {
            let resolved = false;
            let initialFire = true; // Track if this is the initial fire
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              // Ignore the initial fire (current state)
              if (initialFire) {
                initialFire = false;
                return;
              }
              
              // Now listen for the actual change (user becomes null)
              if (!user && !resolved) {
                resolved = true;
                unsubscribe();
                resolve();
              }
            });
            // Timeout after 3 seconds
            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                unsubscribe();
                resolve();
              }
            }, 3000);
          });
          
          // Now sign out
          await signOut(auth);
          
          // Wait for sign out to complete (observer will fire when state changes)
          await signOutPromise;
          
          // Additional wait to ensure state is fully cleared
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Verify we're actually signed out by checking currentUser directly
          let retries = 0;
          while (auth.currentUser && retries < 5) {
            await signOut(auth);
            await new Promise(resolve => setTimeout(resolve, 1000)); // Longer wait
            retries++;
          }
          
          // Final check - if still signed in, something is wrong
          if (auth.currentUser) {
            // Force one more sign out
            await signOut(auth);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }

        // Sign in to Firebase with custom token
        try {
          // CRITICAL: Verify we're signed out before signing in
          if (auth.currentUser) {
            await signOut(auth);
            // Wait longer to ensure sign-out completes
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Double-check
            if (auth.currentUser) {
              throw new Error(`Cannot sign in: previous user ${auth.currentUser.uid} is still signed in. Please refresh the page.`);
            }
          }
          
          // Set up observer BEFORE signing in to catch the state change
          // onAuthStateChanged fires immediately with current state, so we need to ignore that
          let signedInUser = null;
          const signInPromise = new Promise((resolve, reject) => {
            let resolved = false;
            let initialFire = true; // Track if this is the initial fire
            const unsubscribe = onAuthStateChanged(auth, (user) => {
              // Ignore the initial fire (should be null if we signed out properly)
              if (initialFire) {
                initialFire = false;
                // If initial state has a user, that's a problem
                if (user) {
                  resolved = true;
                  unsubscribe();
                  reject(new Error(`Unexpected user ${user.uid} already signed in before sign-in attempt`));
                }
                return;
              }
              
              // Now listen for the actual change (user becomes non-null)
              if (user && !resolved) {
                signedInUser = user;
                resolved = true;
                unsubscribe();
                resolve(user);
              }
            });
            // Timeout after 5 seconds
            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                unsubscribe();
                reject(new Error('chat service sign-in timeout'));
              }
            }, 5000);
          });
          
          // Now sign in
          await signInWithCustomToken(auth, tokenRes.data.token);
          
          // Wait for auth state to update (observer will fire when state changes)
          const user = await signInPromise;
          
          // Additional small wait
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Verify the UID matches our app's user state
          if (!auth.currentUser) {
            throw new Error('service authentication failed - no current user after sign-in');
          }
          
          const actualUid = auth.currentUser.uid;
          if (actualUid !== expectedUserId) {
            // Sign out the wrong user
            await signOut(auth);
            await new Promise(resolve => setTimeout(resolve, 1000));
            throw new Error(`service auth UID mismatch. Expected ${expectedUserId}, got ${actualUid}. This usually means the service token was for a different user or sign-out didn't complete. Please refresh the page.`);
          }
        } catch (signInErr) {
          // Ensure we're signed out on error
          if (auth.currentUser) {
            await signOut(auth).catch(() => {});
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          throw new Error(`Failed to sign in to chat service: ${signInErr.message}`);
        }
        
        setFirebaseInitialized(true);
        setLoading(false);
        isInitializingRef.current = false;
      } catch (err) {
        isInitializingRef.current = false;
        
        // If user logged out during initialization, don't show error
        if (!userProp) {
          setLoading(false);
          setFirebaseInitialized(false);
          return;
        }
        
        // Ensure Firebase is signed out on error
        if (auth?.currentUser) {
          signOut(auth).catch(() => {});
        }
        
        // Show user-friendly error messages
        let errorMessage = 'Unable to connect to chat. Please try again later.';
        
        if (err.response?.status === 401 || err.response?.status === 403) {
          errorMessage = 'Please log in to use chat.';
          navigate('/login', { replace: true });
        } else if (err.response?.status === 503) {
          errorMessage = 'Chat service is temporarily unavailable. Please try again later.';
        } else if (err.message) {
          // Show more specific error if available
          if (err.message.includes('Firebase')) {
            errorMessage = 'chat connection failed. Please check your connection and try again.';
          } else if (err.message.includes('profile')) {
            errorMessage = 'Failed to load user profile. Please refresh the page.';
          } else if (err.message.includes('token')) {
            errorMessage = 'Failed to authenticate with chat service. Please try logging out and back in.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        setLoading(false);
      }
    };

    initializeChat();
  }, [token, userProp?.id, navigate]);

  // Load initial 15 messages
  useEffect(() => {
    if (!firebaseInitialized) return;

    const loadInitialMessages = async () => {
      try {
        const messagesRef = collection(db, 'rooms', 'general', 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(15));
        
        const snapshot = await getDocs(q);
        const messagesList = [];
        let lastDoc = null;

        snapshot.forEach((doc) => {
          messagesList.push({ id: doc.id, ...doc.data() });
          lastDoc = doc;
        });

        // Reverse to show oldest first
        const reversedMessages = messagesList.reverse();
        
        // Clear any existing messages and set fresh ones
        setMessages(reversedMessages);
        messagesRef.current = reversedMessages;
        setLastVisible(lastDoc);
        setHasMore(snapshot.size === 15);
        setLoading(false);
        
        // Scroll to bottom after initial load
        setTimeout(() => {
          const container = messagesContainerRef.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        }, 100);
      } catch (err) {
        setError('Failed to load messages');
        setLoading(false);
      }
    };

    loadInitialMessages();
  }, [firebaseInitialized]);

  // Listen to new messages only (real-time updates) - optimized to reduce reads
  useEffect(() => {
    if (!firebaseInitialized || loading) return;

    const messagesCollection = collection(db, 'rooms', 'general', 'messages');
    // Listen to only the 5 most recent messages to catch new ones (reduces reads)
    const q = query(messagesCollection, orderBy('timestamp', 'desc'), limit(5));

    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          // Process new messages - only skip if from cache on initial load
          if (change.type === 'added') {
            const newMsg = { id: change.doc.id, ...change.doc.data() };
            const isFromCache = change.doc.metadata.fromCache;
            
            // Skip cache-only messages (these are from initial load)
            // But allow cache messages if they're new (happens when offline comes online)
            if (isFromCache && messagesRef.current.length > 0) {
              // Check if this is a truly new message by comparing with existing
              const existingIds = new Set(messagesRef.current.map(m => m.id));
              if (existingIds.has(newMsg.id)) {
                return; // Already have this message
              }
            }
            
            // Update messages state
            setMessages(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              // If message doesn't exist, add it to the end (newest messages go to end)
              if (!existingIds.has(newMsg.id)) {
                // Check if this message should go at the end (newer) or needs sorting
                const mostRecent = prev[prev.length - 1];
                if (!mostRecent || !mostRecent.timestamp) {
                  const updated = [...prev, newMsg];
                  messagesRef.current = updated;
                  return updated;
                }
                
                // Compare timestamps
                const newMsgTime = newMsg.timestamp?.toDate 
                  ? newMsg.timestamp.toDate() 
                  : (newMsg.timestamp ? new Date(newMsg.timestamp) : new Date());
                const mostRecentTime = mostRecent.timestamp?.toDate 
                  ? mostRecent.timestamp.toDate() 
                  : (mostRecent.timestamp ? new Date(mostRecent.timestamp) : new Date(0));
                
                // Add to end if newer or equal
                if (newMsgTime >= mostRecentTime) {
                  const updated = [...prev, newMsg];
                  messagesRef.current = updated;
                  return updated;
                } else {
                  // Older message - insert in correct position (shouldn't happen often)
                  const updated = [...prev, newMsg].sort((a, b) => {
                    const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
                    const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
                    return timeA - timeB;
                  });
                  messagesRef.current = updated;
                  return updated;
                }
              }
              return prev;
            });
          }
        });
      },
      (err) => {
        // Silent error - don't show to user for real-time updates
      }
    );

    unsubscribeRef.current = unsubscribe;
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [firebaseInitialized, loading]);

  const loadOlderMessages = useCallback(async () => {
    // Prevent multiple simultaneous calls
    if (!lastVisible || loadingMore || !hasMore || !firebaseInitialized || paginationCooldownRef.current || loading) {
      return;
    }

    try {
      paginationCooldownRef.current = true;
      setLoadingMore(true);
      
      const messagesCollection = collection(db, 'rooms', 'general', 'messages');
      const q = query(
        messagesCollection,
        orderBy('timestamp', 'desc'),
        startAfter(lastVisible),
        limit(15)
      );

      const snapshot = await getDocs(q);
      const olderMessages = [];

      // Get documents in order (desc by timestamp)
      snapshot.forEach((doc) => {
        olderMessages.push({ id: doc.id, ...doc.data() });
      });

      if (olderMessages.length > 0) {
        // The last document in the snapshot (oldest) should be our new lastVisible
        const newLastVisible = snapshot.docs[snapshot.docs.length - 1];
        
        // Prepend older messages to the top (reverse because they're in desc order)
        const reversedOlder = olderMessages.reverse();
        
        // Use current messages state, not ref (ref might be stale)
        setMessages(prev => {
          const currentIds = new Set(prev.map(m => m.id));
          const newOlderMessages = reversedOlder.filter(msg => !currentIds.has(msg.id));
          
          if (newOlderMessages.length > 0) {
            const container = messagesContainerRef.current;
            const scrollHeightBefore = container ? container.scrollHeight : 0;
            const scrollTopBefore = container ? container.scrollTop : 0;
            
            // Prepend new messages to existing ones
            const updated = [...newOlderMessages, ...prev];
            
            // Maintain scroll position after prepending older messages
            if (container) {
              setTimeout(() => {
                const scrollHeightAfter = container.scrollHeight;
                const scrollDiff = scrollHeightAfter - scrollHeightBefore;
                container.scrollTop = scrollTopBefore + scrollDiff;
              }, 0);
            }
            
            setLastVisible(newLastVisible);
            setHasMore(snapshot.size === 15);
            return updated;
          } else {
            // All were duplicates, but update lastVisible to continue pagination
            setLastVisible(newLastVisible);
            setHasMore(snapshot.size === 15);
            return prev;
          }
        });
      } else {
        setHasMore(false);
      }
    } catch (err) {
      // Error loading older messages - silently continue
    } finally {
      setLoadingMore(false);
      // Cooldown period to prevent rapid triggers
      setTimeout(() => {
        paginationCooldownRef.current = false;
      }, 2000);
    }
  }, [lastVisible, loadingMore, hasMore, firebaseInitialized, loading]);

  // Handle scroll to top for pagination with improved debouncing and threshold
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !firebaseInitialized || loading) return;

    let scrollTimeout;
    let lastTriggerTime = 0;
    const SCROLL_THRESHOLD = 50; // Only trigger when within 50px of top
    const DEBOUNCE_DELAY = 300; // Increased debounce delay
    const MIN_TIME_BETWEEN_TRIGGERS = 2000; // Minimum 2 seconds between triggers

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const now = Date.now();
      
      // Clear previous timeout
      clearTimeout(scrollTimeout);
      
      // Only process if scroll is actually moving upward significantly
      if (currentScrollTop >= lastScrollTopRef.current) {
        lastScrollTopRef.current = currentScrollTop;
        return; // Scrolling down, ignore
      }
      
      lastScrollTopRef.current = currentScrollTop;
      
      // Debounce scroll handler
      scrollTimeout = setTimeout(() => {
        // Check if enough time has passed since last trigger
        if (now - lastTriggerTime < MIN_TIME_BETWEEN_TRIGGERS) {
          return;
        }
        
        // Load more when scrolled within threshold of top
        if (currentScrollTop < SCROLL_THRESHOLD && hasMore && !loadingMore && lastVisible && !paginationCooldownRef.current) {
          lastTriggerTime = now;
          loadOlderMessages();
        }
      }, DEBOUNCE_DELAY);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(scrollTimeout);
      container.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loadingMore, firebaseInitialized, loadOlderMessages, lastVisible, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    // Use app's user state (trust this, not Firebase)
    let currentUser = userProp || user;
    if (!newMessage.trim() || !currentUser || !currentUser.id || sending || !firebaseInitialized) return;

    // Ensure we have the latest profileColor - fetch fresh if needed
    if (!currentUser.profileColor) {
      try {
        const token = localStorage.getItem('token');
        const userRes = await axios.get(`${API}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { _t: Date.now() }
        });
        currentUser = userRes.data;
        setUser(userRes.data);
      } catch (err) {
        // Continue with default color
      }
    }

    // Verify Firebase auth state
    if (!auth || !auth.currentUser) {
      setError('Please log in to send messages.');
      setTimeout(() => {
        setError(null);
        navigate('/login');
      }, 3000);
      return;
    }

    try {
      setSending(true);
      setError(null);

      // Use app's user ID (this is the source of truth)
      const userId = currentUser.id.toString();

      // Verify Firebase auth UID matches our app's user ID
      if (auth.currentUser.uid !== userId) {
        throw new Error('Authentication mismatch - please refresh the page');
      }

      // Ensure we have profileColor
      const profileColor = currentUser.profileColor || "#667eea";

      const messageData = {
        uid: userId, // Use app's user ID (should match Firebase auth UID)
        name: currentUser.name || 'Anonymous',
        level: currentUser.levelInfo?.level || currentUser.level || 1,
        colorTheme: getLevelColorTheme(currentUser.levelInfo?.level || currentUser.level || 1),
        profileColor: profileColor, // Always use profileColor, not level-based
        text: newMessage.trim(),
        timestamp: serverTimestamp()
      };

      const messagesRef = collection(db, 'rooms', 'general', 'messages');
      await addDoc(messagesRef, messageData);

      setNewMessage('');
      
      // Scroll to bottom after sending message
      setTimeout(() => {
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 100);
    } catch (err) {
      let errorMessage = 'Unable to send message. Please try again.';
      
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        errorMessage = 'You do not have permission to send messages. Please log in again.';
        setTimeout(() => {
          setError(null);
          navigate('/login');
        }, 3000);
      } else if (err.message?.includes('mismatch')) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Helper to check if message should show username/timestamp
  const shouldShowHeader = (currentMsg, prevMsg, index) => {
    // First message always shows header
    if (index === 0) return true;
    
    // No previous message
    if (!prevMsg) return true;
    
    // Different user
    if (currentMsg.uid !== prevMsg.uid) return true;
    
    // Check if more than 5 minutes apart
    const currentTime = currentMsg.timestamp?.toDate 
      ? currentMsg.timestamp.toDate() 
      : new Date(currentMsg.timestamp || 0);
    const prevTime = prevMsg.timestamp?.toDate 
      ? prevMsg.timestamp.toDate() 
      : new Date(prevMsg.timestamp || 0);
    
    const diffMs = currentTime - prevTime;
    const diffMins = diffMs / 60000;
    
    // More than 5 minutes apart
    if (diffMins > 5) return true;
    
    return false;
  };

  if (loading) {
    return (
      <div className="chat-page">
        <div className="chat-header">
          <h2>Chat</h2>
        </div>
        <div className="chat-loading">
          <div className="loading-indicator">Loading chat...</div>
        </div>
      </div>
    );
  }

  if (error && !firebaseInitialized) {
    return (
      <div className="chat-page">
        <div className="chat-header">
          <h2>Chat</h2>
        </div>
        <div className="chat-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={() => {
            // Reload page to get a fresh state
            window.location.reload();
          }}>Retry</button>
        </div>
      </div>
    );
  }

  // Helper function to adjust color brightness
  const adjustColorBrightness = (hex, percent) => {
    const num = parseInt(hex.replace("#", ""), 16);
    const r = Math.min(255, Math.max(0, (num >> 16) + percent));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
    const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const currentUser = userProp || user;

  return (
    <div className="chat-page">
      <div className="chat-header">
        <MdForum className="chat-header-icon" />
        <h2>General</h2>
      </div>

      {error && firebaseInitialized && (
        <div className="chat-error-banner">
          {error}
        </div>
      )}

      <div 
        className="chat-messages" 
        ref={messagesContainerRef}
        style={{
          top: error && firebaseInitialized 
            ? `calc(var(--header-height) + 50px + 45px)` 
            : `calc(var(--header-height) + 50px)`
        }}
      >
          {loadingMore && (
            <div className="chat-loading-more">
              <div className="loading-indicator">Loading older messages...</div>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="chat-empty">
              <p>No messages yet. Be the first to say something!</p>
            </div>
          ) : (
            messages.map((msg, index) => {
              const prevMsg = index > 0 ? messages[index - 1] : null;
              const showHeader = shouldShowHeader(msg, prevMsg, index);
              const isGrouped = !showHeader;
              const isNewUser = showHeader && prevMsg && prevMsg.uid !== msg.uid;
              
              const profileColor = msg.profileColor || '#667eea';
              
              return (
                <div 
                  key={msg.id} 
                  className={`chat-message ${msg.uid === (user?.id?.toString() || userProp?.id?.toString()) ? 'own-message' : ''} ${isGrouped ? 'grouped-message' : ''} ${isNewUser ? 'new-user-message' : ''}`}
                >
                  <div className="message-content-wrapper">
                    {showHeader ? (
                      <div 
                        className="message-avatar"
                        style={{
                          background: `linear-gradient(135deg, ${profileColor} 0%, ${adjustColorBrightness(profileColor, -20)} 100%)`
                        }}
                      >
                        <span>{msg.name.charAt(0).toUpperCase()}</span>
                      </div>
                    ) : (
                      <div className="message-avatar-spacer"></div>
                    )}
                    <div className="message-content">
                      {showHeader && (
                        <div className="message-header">
                          <button
                            className="message-username-btn"
                            onClick={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setPopupPosition({
                                top: rect.bottom + 8,
                                left: rect.left
                              });
                              setSelectedUser({ name: msg.name, level: msg.level || 1, colorTheme: msg.colorTheme || '#667eea' });
                            }}
                            style={{ color: msg.colorTheme || '#667eea' }}
                          >
                            {msg.name}
                          </button>
                          <span className="message-timestamp">{formatTimestamp(msg.timestamp)}</span>
                        </div>
                      )}
                      <div className="message-text">
                        {msg.text}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

      <form className="chat-input-form" onSubmit={handleSendMessage}>
          <input
            type="text"
            className="chat-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            maxLength={500}
            disabled={sending || !firebaseInitialized}
          />
          <button 
            type="submit" 
            className="chat-send-button"
            disabled={!newMessage.trim() || sending || !firebaseInitialized}
          >
            <MdSend />
          </button>
        </form>

      {/* User Info Popup */}
      {selectedUser && (
        <>
          <div className="user-info-popup-overlay" onClick={() => setSelectedUser(null)} />
          <div 
            className="user-info-popup"
            style={{
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="user-info-popup-close"
              onClick={() => setSelectedUser(null)}
            >
              <MdClose />
            </button>
            <div className="user-info-content">
              <div 
                className="user-info-username"
                style={{ color: selectedUser.colorTheme }}
              >
                {selectedUser.name}
              </div>
              <div className="user-info-level">
                <span 
                  className="user-info-level-badge"
                  style={{ 
                    backgroundColor: selectedUser.colorTheme,
                    color: '#ffffff'
                  }}
                >
                  Level {selectedUser.level}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

